import { isValidPhoneNumber } from 'react-phone-number-input'
import { z } from 'zod'

import type { Member } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { useUpdateMember } from '@/frontend/hooks/api/use-update-member'

import { US_STATES } from './us-states'

const updateMemberSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.email('A valid email is required'),
  phone: z.string(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().nullable(),
})

type UpdateMemberFormValues = z.infer<typeof updateMemberSchema>

type UpdateMemberFormProps = {
  member: Member
  onSuccess?: () => void
  onCancel?: () => void
}

/** Form for editing a member's contact information. */
export function UpdateMemberForm({ member, onSuccess, onCancel }: UpdateMemberFormProps) {
  const { mutateAsync: updateMember } = useUpdateMember(member.id)
  // Address layout follows the member's own (immutable) region: US members edit a state, UK
  // members edit a country.
  const isUsMember = member.region === 'us'

  const form = useAppForm({
    defaultValues: {
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      phone: member.phone,
      address1: member.address1,
      address2: member.address2,
      city: member.city,
      postalCode: member.postalCode,
      state: member.state,
      country: member.country,
    } as UpdateMemberFormValues,
    // Phone validation is scoped to *changes* only: when the value in the field matches the
    // member's stored phone, the field passes without inspection so staff can edit unrelated
    // fields (e.g. address) for members whose stored phone is in a legacy Digitail format
    // that wouldn't pass a strict E.164 check. A deliberate edit re-enters the
    // isValidPhoneNumber gate, and the backend PATCH route enforces the same rule server-side.
    validators: {
      onSubmit: updateMemberSchema.superRefine((value, ctx) => {
        if (value.phone === member.phone) return
        if (!isValidPhoneNumber(value.phone)) {
          ctx.addIssue({
            code: 'custom',
            path: ['phone'],
            message: 'Please enter a valid phone number',
          })
        }
      }),
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        await updateMember({
          firstName: value.firstName,
          lastName: value.lastName,
          email: value.email,
          // Skip sending phone entirely when unchanged - the backend only re-validates on a
          // real change, so omitting the field lets legacy-format numbers pass through
          // untouched during unrelated edits.
          ...(value.phone !== member.phone && { phone: value.phone }),
          address1: value.address1 || null,
          address2: value.address2 || null,
          city: value.city || null,
          postalCode: value.postalCode || null,
          state: isUsMember ? value.state || null : null,
          country: isUsMember ? null : value.country || null,
        })
        onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to update contact information',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-3">
          <form.GlobalError />

          <div className="flex gap-3">
            <form.AppField name="firstName">
              {(field) => <field.InputField label="First name" layoutClassName="flex-1" required />}
            </form.AppField>
            <form.AppField name="lastName">
              {(field) => <field.InputField label="Last name" layoutClassName="flex-1" required />}
            </form.AppField>
          </div>

          <div className="flex gap-3">
            <form.AppField name="email">
              {(field) => (
                <field.InputField label="Email" type="email" layoutClassName="flex-1" required />
              )}
            </form.AppField>
            <form.AppField name="phone">
              {(field) => (
                <field.PhoneInputField
                  label="Phone"
                  autoComplete="tel"
                  defaultCountry={isUsMember ? 'US' : 'GB'}
                  layoutClassName="flex-1"
                />
              )}
            </form.AppField>
          </div>

          <form.AppField name="address1">
            {(field) => <field.InputField label="Address line 1" />}
          </form.AppField>

          <form.AppField name="address2">
            {(field) => <field.InputField label="Address line 2" />}
          </form.AppField>

          <div className="flex gap-3">
            <form.AppField name="city">
              {(field) => <field.InputField label="City" layoutClassName="flex-1" />}
            </form.AppField>
            <form.AppField name="postalCode">
              {(field) => (
                <field.InputField
                  label={isUsMember ? 'ZIP code' : 'Postal code'}
                  layoutClassName="flex-1"
                />
              )}
            </form.AppField>
            {isUsMember ? (
              <form.AppField name="state">
                {(field) => (
                  <field.ComboboxField
                    label="State"
                    placeholder="Select state"
                    layoutClassName="flex-1"
                    options={US_STATES}
                    getKey={(s) => s.code}
                    getLabel={(s) => s.name}
                  />
                )}
              </form.AppField>
            ) : (
              <form.AppField name="country">
                {(field) => <field.InputField label="Country" layoutClassName="flex-1" />}
              </form.AppField>
            )}
          </div>
        </div>

        <div className="pt-3 flex items-center gap-x-2 justify-end">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  )
}
